import { Trans, useLingui } from '@lingui/react/macro';
import { useSearch } from '@tanstack/react-router';
import type { HTMLProps, ReactNode } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Typography } from '@remarkable/ark-web';

import { BillingInfoCountryChanged } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type {
  CloudConnectAddress,
  CloudConnectSubscription,
} from 'src/api/endpoints/cloudConnect.types';
import { useUpdateCloudConnectAddress } from 'src/api/queries';
import {
  Button,
  CountryPicker,
  Form,
  Input,
  Modal,
  NotificationBox,
  StatePicker,
} from 'src/components';
import { cn } from 'src/utils/classNamesHelper';
import { getSupportedCountries } from 'src/utils/countryList';

interface EditCloudConnectBillingInfoFormProps extends HTMLProps<HTMLDivElement> {
  subscription: CloudConnectSubscription;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const getDefaultValues = (
  address: CloudConnectSubscription['address']
): CloudConnectAddress => ({
  line1: address.line1,
  line2: address.line2,
  city: address.city,
  state: address.state,
  postalCode: address.postalCode,
  country: address.country,
});

const EditCloudConnectBillingInfoForm = ({
  subscription,
  onSuccess,
  onCancel,
  ...props
}: EditCloudConnectBillingInfoFormProps) => {
  const { t } = useLingui();
  const updateAddress = useUpdateCloudConnectAddress();
  const supportedCountries = getSupportedCountries();
  const isUS = subscription.address.country === 'US';
  const form = useForm<CloudConnectAddress>({
    defaultValues: getDefaultValues(subscription.address),
  });

  const onSubmit = (data: CloudConnectAddress) => {
    updateAddress.mutate(
      {
        line1: data.line1,
        line2: data.line2,
        city: data.city,
        postalCode: data.postalCode,
        state: data.state || subscription.address.state,
        country: subscription.address.country,
      },
      { onSuccess }
    );
  };

  return (
    <div {...props} className={cn('text-left', props.className)}>
      <Form onSubmit={onSubmit} {...form} className="flex flex-col gap-24">
        <Input
          data-cy="billing-address1"
          label={t`Billing address 1`}
          autoComplete="address-level1"
          name="line1"
          focusOnIsEditable
        />

        <Input
          data-cy="billing-address2"
          label={t`Billing address 2`}
          autoComplete="address-level2"
          name="line2"
        />

        <div className="flex flex-col gap-16 sm:flex-row">
          <Input
            className="flex-1"
            data-cy="billing-zip"
            label={t`Zip`}
            autoComplete="zip"
            name="postalCode"
          />

          <Input
            className="flex-1"
            data-cy="billing-city"
            label={t`City`}
            autoComplete="city"
            name="city"
          />
        </div>

        <div className="flex flex-col gap-16 sm:flex-row">
          <StatePicker
            disabled={!!subscription.address.state}
            className="flex-1"
            name="state"
            countryFieldName="country"
            data-cy="billing-state"
          />
          <Controller
            control={form.control}
            name="country"
            render={({ field }) => (
              <CountryPicker
                disabled={!!subscription.address.country}
                className="flex-1"
                data-cy="billing-country"
                countries={supportedCountries}
                onChange={(e) => {
                  const oldValue = field.value || 'unknown';
                  const newValue = e.target.value;

                  tracker.trackEvent(
                    new BillingInfoCountryChanged({
                      from_country: oldValue,
                      to_country: newValue,
                    })
                  );
                }}
                value={field.value}
                name={field.name}
              />
            )}
          />
        </div>

        <NotificationBox
          variant="info"
          title={
            isUS
              ? t`Changing your country or state isn't possible at this time.`
              : t`Changing your country isn't possible at this time.`
          }
          data-cy="billing-country-note"
          muted
        />

        <div className="mt-24 flex justify-center gap-16">
          <Modal.Close asChild>
            <Button
              type="button"
              onClick={() => {
                form.reset();
                updateAddress.reset();
                onCancel?.();
              }}
              variant="secondary"
              data-cy="cancel-edit-billing-info-button"
              analytics={{
                text: 'Cancel',
                location: ComponentLocations.MANAGE_CONNECT.MAIN,
                action: 'cancel edit billing info',
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </Modal.Close>

          <Button
            type="submit"
            loading={updateAddress.isPending}
            data-cy="save-billing-info-button"
            disabled={!form.formState.isDirty}
            analytics={{
              text: 'Save',
              location: ComponentLocations.MANAGE_CONNECT.MAIN,
              action: 'save billing info',
            }}
          >
            <Trans>Save</Trans>
          </Button>
        </div>

        {updateAddress.isError && (
          <NotificationBox
            className="mt-24"
            error={updateAddress.error}
            title={t`Unable to update billing info`}
            data-cy="update-billing-info-error"
          >
            <Typography variant="body-md">
              <Trans>Please check if your information is correct</Trans>
            </Typography>
          </NotificationBox>
        )}
      </Form>
    </div>
  );
};

export const CloudConnectBillingInfoModal = ({
  subscription,
  trigger,
}: {
  subscription: CloudConnectSubscription;
  trigger: ReactNode;
}) => {
  const { t } = useLingui();
  const search = useSearch({ strict: false });
  const [open, setOpen] = useState(!!search.openBillingInfo);

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <Modal.Trigger asChild>{trigger}</Modal.Trigger>

      <Modal.Content title={t`Billing information`}>
        <EditCloudConnectBillingInfoForm
          subscription={subscription}
          onSuccess={() => setOpen(false)}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
